import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowsOut, Stack, Monitor, Sparkle, ShareNetwork, X, DownloadSimple, Notebook, PencilSimple, FloppyDisk, Graph, UploadSimple, Users, ChatCircle } from '@phosphor-icons/react';
import ShareDialog from './ShareDialog';
import MembersDialog from './MembersDialog';
// BuildModePicker + setDeckMode removed in generic-viewer refactor
// (PP main #87 alignment, Stage 4). The agent picks build mode per
// turn from user intent; no deck-level pre-pick.
import ConceptCloud from './ConceptCloud';
import type { GatherAudience, GatherGoal } from './gather/GatherCards';
import MarkdownPlanEditor from './plan/MarkdownPlanEditor';
import StoryboardPanel, { type StoryCard } from './plan/StoryboardPanel';
import { parseStoryboard, serializeStoryboard } from '../utils/storyboardData';
import type { Phase } from '../types';
import { notifyError } from '../hooks/useNotify';
import { apiFetch, AuthExpiredError } from '../api/auth-fetch';
import { getVersion, saveSlide, exportPptxUrl, type SlideRow } from '../api/client';
import { marked } from 'marked';

type TabKey = 'brief' | 'storyboard' | 'notes' | 'presentation';
type ExportKind = 'html';

interface ExportProgressState {
  kind: ExportKind;
  label: string;
  progress: number;
  status: 'running' | 'complete' | 'error';
}

interface PreviewPanelProps {
  phase: Phase;
  deckId: string | null;
  /** Workspace the deck lives in; used by child dialogs that need the
   *  workspace-scoped tulpa registry (MembersDialog → /tulpas?ws=…). */
  workspaceId?: string | null;
  deckTitle?: string;
  /** Build mode set on the deck. null = not yet picked (legacy or fresh) */
  deckMode?: 'html' | 'pptx' | null;
  /** PPTX theme name when mode === 'pptx' */
  deckTheme?: 'dify-default' | 'dify-dark' | null;
  deckCurrentVersion?: string | null;
  /** Refetch the deck after BuildModePicker submits so deckMode/Theme update */
  onDeckChanged?: () => void;
  isGenerating: boolean;
  currentActivity: string;
  onAdvancePhase?: (phase: Phase) => void;
  planMarkdown: string;
  onPlanMarkdownChange: (md: string) => void;
  audienceInfo: GatherAudience | null;
  goalInfo: GatherGoal | null;
  keyMessages: string[] | null;
  maxPhaseReached?: Phase;
  previewRefreshKey?: number;
  contextVersion?: number;
  /** Parent-prefetched context items, threaded to ConceptCloud so it
   *  doesn't re-fetch listContext on mount. Eliminates a ~2.4s
   *  duplicate on the deck-open hot path. */
  contextItems?: { key: string; content: string }[];
  /** Deck id for which contextItems reflects a completed parent fetch.
   *  Lets ConceptCloud distinguish "parent still loading" from
   *  "parent loaded; this deck genuinely has no context". */
  contextHydratedDeckId?: string | null;
  planTask?: 'create' | 'update' | null;
  previewTask?: 'create' | 'update' | null;
  notesTask?: 'create' | 'update' | null;
  planSaveStatus?: 'idle' | 'saving' | 'saved';
  planUpdatedAt?: string | number;
  planVersions?: { key: string; label: string }[];
  activePlanKey?: string;
  onSelectPlanVersion?: (key: string) => void;
  onPlanSave?: (md: string) => void;
  notesMarkdown: string;
  onNotesChange: (md: string) => void;
  onNotesSave?: (md: string) => void;
  notesSaveStatus?: 'idle' | 'saving' | 'saved';
  notesUpdatedAt?: string | number;
  notesVersions?: { key: string; label: string }[];
  activeNotesKey?: string;
  onSelectNotesVersion?: (key: string) => void;
  previewGeneratingNoHTML?: boolean;
  artifacts?: { filename: string; key: string; updated_at?: string | number }[];
  activeArtifactKey?: string | null;
  onSelectArtifact?: (key: string) => void;
  activeSlideHash?: string;
  onActiveTabChange?: (tab: TabKey) => void;
  /**
   * Artifact view overlay. When set, the panel renders a layer on top of
   * the deck-html iframe with file metadata + Download button (PPTX) or
   * an inline iframe (image/PDF). The deck iframe stays mounted underneath
   * so returning preserves its scroll/slide state. `null` = no overlay.
   */
  artifactView?: { fileHash?: string; fileUrl?: string; filename: string; fileMime?: string; fileSize?: number } | null;
  onCloseArtifactView?: () => void;
  /**
   * Publish the assembled HTML deck to a Droploft connection. The
   * parent owns the actual fetch + toast surfacing; passing the
   * callback enables the toolbar button. `undefined` = hide it (no
   * Droploft connection yet, or build mode != html).
   */
  onPublishToDroploft?: () => void;
  droploftPublishing?: boolean;
  /** Generic artifact viewer (PP main #87 alignment). Entries from
   *  signals.artifacts; PreviewTab picks the active one and uses its
   *  preview_url for the iframe. Falls back to legacy keyParam URL
   *  when undefined / empty. */
  artifactSignals?: import('../types').ArtifactSignal[];
  activeArtifactSignalId?: string | null;
}

const TABS: { key: TabKey; label: string; icon: React.FC<any> }[] = [
  { key: 'brief', label: 'Brief', icon: Graph },
  { key: 'storyboard', label: 'Storyboard', icon: Stack },
  { key: 'notes', label: 'Speaker Notes', icon: Notebook },
  { key: 'presentation', label: 'Presentation', icon: Monitor },
];

// Extract the first `# Heading` from markdown content, stripping a YAML
// frontmatter block first so `---\nname: x\n---` doesn't get mistaken
// for the title. Returns at most 60 characters (tabs are visually
// constrained), or null if no heading found.
// (aligns PP main #129 — Storyboard tab renames to doc title)
function extractMarkdownTitle(md: string | null | undefined): string | null {
  if (typeof md !== 'string' || !md.trim()) return null;
  let body = md;
  if (body.startsWith('---\n') || body.startsWith('---\r\n')) {
    const end = body.indexOf('\n---', 4);
    if (end > 0) body = body.slice(end + 4).replace(/^\r?\n/, '');
  }
  const m = body.match(/^#\s+(.+)$/m);
  if (!m) return null;
  const title = m[1].trim().slice(0, 60);
  return title.length > 0 ? title : null;
}

export default function PreviewPanel({
  phase, deckId, workspaceId, deckTitle, deckMode, deckTheme, deckCurrentVersion, onDeckChanged,
  isGenerating, currentActivity, onAdvancePhase,
  planMarkdown, onPlanMarkdownChange, onPlanSave,
  audienceInfo, goalInfo, keyMessages, maxPhaseReached, previewRefreshKey, contextVersion, contextItems, contextHydratedDeckId,
  planTask, previewTask, notesTask, planSaveStatus, planUpdatedAt,
  planVersions = [], activePlanKey, onSelectPlanVersion,
  notesMarkdown, onNotesChange, onNotesSave, notesSaveStatus, notesUpdatedAt,
  notesVersions = [], activeNotesKey, onSelectNotesVersion,
  previewGeneratingNoHTML, artifacts = [], activeArtifactKey, onSelectArtifact, activeSlideHash, onActiveTabChange,
  artifactView = null, onCloseArtifactView,
  onPublishToDroploft, droploftPublishing = false,
  artifactSignals, activeArtifactSignalId,
}: PreviewPanelProps) {
  const phaseToTab = (p: Phase | string): TabKey => {
    if (p === 'preview') return 'presentation';
    if (p === 'plan' || p === 'plan-ready') return 'storyboard';
    return 'brief'; // gather phase
  };
  const [activeTab, setActiveTab] = useState<TabKey>(phaseToTab(phase));
  const [showBriefing, setShowBriefing] = useState(false);

  const userPickedTabRef = useRef(false);
  const selectTab = useCallback((k: TabKey) => {
    userPickedTabRef.current = true;
    setActiveTab(k);
  }, []);

  useEffect(() => {
    userPickedTabRef.current = false;
    setActiveTab(phaseToTab(phase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId]);

  // Follow derived phase until the user manually picks a tab.
  useEffect(() => {
    if (!userPickedTabRef.current) setActiveTab(phaseToTab(phase));
  }, [phase]);

  // Notify parent when active tab changes (for layout ratio adjustment)
  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  const PHASE_ORDER: Phase[] = ['plan', 'plan-ready', 'preview'];
  const pIdx = (p: Phase) => PHASE_ORDER.indexOf(p);
  const hasReachedPreview = pIdx(maxPhaseReached || phase) >= pIdx('preview');

  // Build-mode picker fully retired in Stage 4 of #87 alignment.
  // The agent picks per-turn from user intent; safeAdvancePhase is
  // now a straight pass-through.
  const safeAdvancePhase = useCallback((target: Phase) => {
    onAdvancePhase?.(target);
  }, [onAdvancePhase]);

  // Auto-switch to presentation while building, unless the user has taken
  // manual control of the right-panel tab.
  const prevPreviewTask = useRef(previewTask);
  useEffect(() => {
    if (!userPickedTabRef.current) {
      if (previewTask === 'create' && !prevPreviewTask.current) {
        setActiveTab('presentation');
      }
      const wasBuilding = !!prevPreviewTask.current;
      const nowDone = !previewTask;
      if (wasBuilding && nowDone && hasReachedPreview) {
        setActiveTab('presentation');
      }
    }
    prevPreviewTask.current = previewTask;
  }, [previewTask, hasReachedPreview]);

  const tabActionStyle: React.CSSProperties = {
    padding: '3px 8px', background: 'none', border: '1px solid var(--border)',
    borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
    color: 'var(--text-muted)', fontSize: 'var(--fs-2xs)', fontFamily: 'inherit', transition: 'all 0.15s',
  };

  // Editor edit mode — shared by storyboard and notes tabs
  const [mdEditing, setMdEditing] = useState(false);
  const [mdDirty, setMdDirty] = useState(false);
  const mdSnapshotRef = React.useRef('');

  const startEditing = () => {
    mdSnapshotRef.current = activeTab === 'storyboard' ? planMarkdown : notesMarkdown;
    setMdEditing(true);
    setMdDirty(false);
  };
  const saveEditing = () => {
    const md = activeTab === 'storyboard' ? planMarkdown : notesMarkdown;
    const saveFn = activeTab === 'storyboard' ? onPlanSave : onNotesSave;
    saveFn?.(md);
    setMdEditing(false);
    setMdDirty(false);
    const what = activeTab === 'storyboard' ? 'storyboard' : 'speaker notes';
    const msg = activeTab === 'storyboard' ? 'I updated the storyboard' : 'I updated the speaker notes';
    window.dispatchEvent(new CustomEvent('pp:edit-saved', { detail: { what: msg } }));
  };
  const cancelEditing = () => {
    const changeFn = activeTab === 'storyboard' ? onPlanMarkdownChange : onNotesChange;
    changeFn(mdSnapshotRef.current);
    setMdEditing(false);
    setMdDirty(false);
  };
  // Reset edit mode when switching tabs
  React.useEffect(() => { setMdEditing(false); setMdDirty(false); }, [activeTab]);

  const hasPreview = (previewRefreshKey || 0) > 0 || hasReachedPreview;

  // Parse storyboard from planMarkdown — if it has the storyboard header, render card view
  const storyboardData = useMemo(() => {
    if (!planMarkdown || !planMarkdown.includes('=== STORYBOARD ===')) return null;
    const parsed = parseStoryboard(planMarkdown);
    if (parsed.slides.length === 0) return null;
    return parsed;
  }, [planMarkdown]);

  const storyCards: StoryCard[] = useMemo(() => {
    if (!storyboardData) return [];
    return storyboardData.slides.map(s => ({
      id: `slide-${s.num}`,
      slideNum: s.num,
      title: s.title,
      body: s.body.join('\n'),
      tag: s.type,
    }));
  }, [storyboardData]);

  const handleCardsChange = useCallback((cards: StoryCard[]) => {
    if (!storyboardData) return;
    const updatedSlides = cards.map(c => ({
      num: c.slideNum,
      type: c.tag,
      title: c.title,
      body: c.body.split('\n'),
      notes: storyboardData.slides.find(s => s.num === c.slideNum)?.notes || [],
    }));
    const md = serializeStoryboard({ meta: storyboardData.meta, slides: updatedSlides });
    onPlanMarkdownChange(md);
  }, [storyboardData, onPlanMarkdownChange]);

  // CF-native storyboard source: when the agent has materialised a
  // presentation version, fetch its slides and render those as
  // cards. This takes priority over PP's planMarkdown-derived
  // storyboard once a version exists.
  const [cfSlides, setCfSlides] = useState<SlideRow[]>([]);
  const [cfVersionName, setCfVersionName] = useState<string>('');
  useEffect(() => {
    if (!deckId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/decks/${deckId}/versions`);
        const d = await r.json().catch(() => null);
        if (cancelled) return;
        const current = d?.data?.current;
        if (!d?.ok || !current) {
          setCfSlides([]);
          setCfVersionName('');
          return;
        }
        setCfVersionName(current);
        const v = await getVersion(deckId, current);
        if (!cancelled) setCfSlides(v.slides);
      } catch {
        if (!cancelled) setCfSlides([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deckId, previewRefreshKey]);

  const cfStoryCards: StoryCard[] = useMemo(
    () =>
      cfSlides.map((s) => ({
        id: `${cfVersionName}-${s.slide_num}`,
        slideNum: s.slide_num,
        title: s.title,
        body: s.body,
        tag: '',
      })),
    [cfSlides, cfVersionName],
  );

  const visibleTabKeys = useMemo<TabKey[]>(() => {
    const hasStoryboard = !!planMarkdown || !!planTask || cfStoryCards.length > 0;
    const hasNotes = !!notesMarkdown || !!notesTask;
    const hasPres = hasPreview || !!previewTask || phase === 'preview' || cfStoryCards.length > 0;
    return TABS.filter((t) =>
      (t.key === 'brief' && !!deckId) ||
      (t.key === 'storyboard' && (hasStoryboard || phase === 'plan-ready' || phase === 'preview')) ||
      (t.key === 'notes' && hasNotes) ||
      (t.key === 'presentation' && hasPres),
    ).map((t) => t.key);
  }, [deckId, planMarkdown, planTask, cfStoryCards.length, notesMarkdown, notesTask, hasPreview, previewTask, phase]);

  useEffect(() => {
    if (visibleTabKeys.length && !visibleTabKeys.includes(activeTab)) {
      setActiveTab(visibleTabKeys[visibleTabKeys.length - 1]);
    }
  }, [visibleTabKeys, activeTab]);

  const handleCfCardsChange = useCallback(
    (cards: StoryCard[]) => {
      if (!deckId || !cfVersionName) return;
      // Title/body edits → per-slide saveSlide. Reorder/delete in
      // the card view stays local-only for now; the iframe HTML
      // editor is the authoritative reorder path.
      const beforeBy = new Map(cfSlides.map((s) => [s.slide_num, s]));
      cards.forEach((c) => {
        const before = beforeBy.get(c.slideNum);
        const titleChanged = !before || before.title !== c.title;
        const bodyChanged = !before || before.body !== c.body;
        if (!titleChanged && !bodyChanged) return;
        const patch: { slide_num: number; title?: string; body?: string } = {
          slide_num: c.slideNum,
        };
        if (titleChanged) patch.title = c.title;
        if (bodyChanged) patch.body = c.body;
        saveSlide(deckId, cfVersionName, patch).catch((err) => {
          if (err instanceof AuthExpiredError) return;
          notifyError(
            `Save slide failed: ${(err as Error)?.message || 'network error'}`,
          );
        });
      });
      // Optimistic local merge.
      setCfSlides(
        cards.map((c) => ({
          presentation_name: cfVersionName,
          slide_num: c.slideNum,
          title: c.title,
          body: c.body,
          updated_at: Date.now(),
        })),
      );
    },
    [deckId, cfVersionName, cfSlides],
  );

  // Shared activity bubble — one for all tasks, lives at panel level
  const activeTaskName = planTask ? `plan:${planTask}` : notesTask ? `notes:${notesTask}` : previewTask ? `preview:${previewTask}` : null;
  const taskLabel = planTask ? (planTask === 'create' ? 'Planning...' : 'Updating plan...')
    : notesTask ? (notesTask === 'create' ? 'Writing notes...' : 'Updating notes...')
    : previewTask ? (previewTask === 'create' ? 'Building slides...' : 'Updating slides...')
    : null;
  const [taskElapsed, setTaskElapsed] = useState(0);
  useEffect(() => {
    if (!activeTaskName) { setTaskElapsed(0); return; }
    const t0 = Date.now();
    const iv = setInterval(() => setTaskElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [activeTaskName]);
  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', background: 'var(--bg-primary)' }}>
      {/* Shared activity bubble — centered at bottom */}
      {activeTaskName && (
        <div style={{
          position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 20,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 16px',
          background: 'rgba(0,0,0,0.75)',
          borderRadius: 16,
          boxShadow: 'none',
          pointerEvents: 'none',
          animation: 'bubble-in 0.3s ease',
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            border: '1.5px solid rgba(255,255,255,0.5)',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.9)', fontWeight: 300 }}>
            {taskLabel || 'Working...'}
          </span>
          <span style={{ fontSize: 'var(--fs-2xs)', color: 'rgba(255,255,255,0.35)', fontVariantNumeric: 'tabular-nums' }}>
            {formatElapsed(taskElapsed)}
          </span>
        </div>
      )}

      <style>{`@keyframes bubble-in { from { opacity: 0; transform: translateX(-50%) translateY(6px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>

      {/* ── Panel content: concept cloud OR tabs ── */}
      {(() => {
        const storyboardTitle = extractMarkdownTitle(planMarkdown);
        const visibleTabs = TABS.filter(t => visibleTabKeys.includes(t.key)).map(t =>
          t.key === 'storyboard' && storyboardTitle
            ? { ...t, label: storyboardTitle }
            : t,
        );

        // If activeTab isn't visible, auto-select the best one
        const currentTabVisible = visibleTabs.some(t => t.key === activeTab);
        const effectiveTab = currentTabVisible ? activeTab : visibleTabs[visibleTabs.length - 1]?.key || 'storyboard';

        return (
          <>
            {/* Tab bar */}
            <div style={{
              height: 40, display: 'flex', alignItems: 'center',
              padding: '0 12px 0 12px',
              background: 'var(--bg-surface)', flexShrink: 0,
            }}>
              <div className="tab-scroll" style={{ display: 'flex', gap: 2, overflowX: 'auto', flex: 1, minWidth: 0, scrollbarWidth: 'none' }}>
              {visibleTabs.map(tab => {
                const isActive = effectiveTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => selectTab(tab.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 14px', fontSize: 'var(--fs-2xs)', fontWeight: isActive ? 500 : 400,
                      background: 'transparent',
                      color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--text-primary)' : '2px solid transparent',
                      borderRadius: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit', transition: 'all 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
              </div>

              {/* Panel-specific actions — fused into tab bar */}
              <div className="pp-tab-actions" style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingLeft: 8, paddingRight: 4 }}>
                {effectiveTab === 'presentation' && (
                  <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                    HTML artifact preview
                  </span>
                )}
                {/* Build mode pill — visible on every tab EXCEPT
                    preview. The preview tab renders its own action
                    group that absolutely floats up into this same
                    row at `top:-32; right:4` (Versions / Edit /
                    HTML / PPTX / PDF / Share / Fullscreen). The two
                    right-edge groups would otherwise overlap — the
                    pill ends up sitting underneath Share/Fullscreen.
                    On preview tab the mode is implied by the inner
                    toolbar (PPTX-download vs HTML-download); the
                    user can switch to Storyboard or ConceptCloud
                    to change mode if needed.

                    Stage 1 of PP main #87 alignment: Pick mode pill
                    hidden — the agent picks per-turn, no user pre-pick
                    needed. Pill removal stage; full generic-viewer
                    refactor in upcoming stage. */}
                {/* Save status for storyboard/notes */}
                {(effectiveTab === 'storyboard' || effectiveTab === 'notes') && (() => {
                  const status = effectiveTab === 'storyboard' ? planSaveStatus : notesSaveStatus;
                  return status === 'saving' ? (
                    <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>Saving...</span>
                  ) : status === 'saved' ? (
                    <span style={{ fontSize: 'var(--fs-2xs)', color: '#16a34a' }}>Saved</span>
                  ) : null;
                })()}

                {/* Edit / Save / Cancel for storyboard/notes */}
                {(effectiveTab === 'storyboard' || effectiveTab === 'notes') && (() => {
                  const hasContent = effectiveTab === 'storyboard' ? !!planMarkdown : !!notesMarkdown;
                  if (!hasContent) return null;
                  const saving = (effectiveTab === 'storyboard' ? planSaveStatus : notesSaveStatus) === 'saving';
                  if (mdEditing) return (
                    <>
                      <button onClick={saveEditing} disabled={!mdDirty || saving} style={{ ...tabActionStyle, background: '#0033FF', borderColor: '#0033FF', color: '#fff', fontWeight: 600, opacity: (!mdDirty || saving) ? 0.5 : 1 }}>
                        <FloppyDisk size={12} weight="bold" /> {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={cancelEditing} style={tabActionStyle}>Cancel</button>
                    </>
                  );
                  return <button onClick={startEditing} style={tabActionStyle}><PencilSimple size={12} /> Edit</button>;
                })()}

                {/* MD download for storyboard/notes */}
                {effectiveTab === 'storyboard' && planMarkdown && (
                  <button onClick={() => {
                    const filename = activePlanKey?.split('/').pop() || 'storyboard.md';
                    const blob = new Blob([`# Plan\n\n${planMarkdown}`], { type: 'text/markdown' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
                  }} style={tabActionStyle} title="Download">
                    <DownloadSimple size={12} weight="light" /> MD
                  </button>
                )}
                {effectiveTab === 'notes' && notesMarkdown && (
                  <button onClick={() => {
                    const filename = activeNotesKey?.split('/').pop() || 'speaker-notes.md';
                    const blob = new Blob([`# Speaker Notes\n\n${notesMarkdown}`], { type: 'text/markdown' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
                  }} style={tabActionStyle} title="Download">
                    <DownloadSimple size={12} weight="light" /> MD
                  </button>
                )}
              </div>
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {effectiveTab === 'brief' && visibleTabKeys.includes('brief') && (
                <ConceptCloud
                  deckId={deckId || ''}
                  deckTitle={deckTitle}
                  audienceInfo={audienceInfo}
                  goalInfo={goalInfo}
                  keyMessages={keyMessages}
                  onClose={() => {}}
                  contextVersion={contextVersion}
                  prefetchedContext={contextItems}
                  contextHydratedDeckId={contextHydratedDeckId}
                />
              )}

              {effectiveTab === 'storyboard' && (
                cfStoryCards.length > 0 ? (
                  <StoryboardPanel
                    cards={cfStoryCards}
                    isGenerating={!!previewTask || !!planTask}
                    currentActivity={currentActivity}
                    onCardsChange={handleCfCardsChange}
                    onApproveGenerate={() => safeAdvancePhase('preview')}
                    hasGeneratedBefore={hasReachedPreview}
                  />
                ) : storyCards.length > 0 ? (
                  <StoryboardPanel
                    cards={storyCards}
                    audience={storyboardData?.meta.audience}
                    narrative={storyboardData?.meta.narrative}
                    isGenerating={!!planTask}
                    currentActivity={currentActivity}
                    onCardsChange={handleCardsChange}
                    onApproveGenerate={() => safeAdvancePhase('preview')}
                    hasGeneratedBefore={hasReachedPreview}
                  />
                ) : (
                  <MarkdownPlanEditor
                    markdown={planMarkdown}
                    onChange={(md) => { onPlanMarkdownChange(md); setMdDirty(md !== mdSnapshotRef.current); }}
                    onSave={onPlanSave}
                    onApprove={() => safeAdvancePhase('preview')}
                    isGenerating={isGenerating}
                    currentActivity={currentActivity}
                    hasBuiltBefore={hasReachedPreview}
                    planTask={planTask}
                    saveStatus={planSaveStatus}
                    updatedAt={planUpdatedAt}
                    hideToolbar
                    editable={mdEditing}
                    downloadFilename="storyboard.md"
                    versions={planVersions}
                    activeVersionKey={activePlanKey}
                    onSelectVersion={onSelectPlanVersion}
                  />
                )
              )}

              {effectiveTab === 'notes' && (
                <MarkdownPlanEditor
                  markdown={notesMarkdown}
                  onChange={(md) => { onNotesChange(md); setMdDirty(md !== mdSnapshotRef.current); }}
                  onSave={onNotesSave}
                  onApprove={() => {}}
                  isGenerating={isGenerating}
                  currentActivity={currentActivity}
                  planTask={notesTask}
                  saveStatus={notesSaveStatus}
                  hideApproveButton
                  editable={mdEditing}
                  title="Speaker Notes"
                  placeholder="Speaker notes will appear here..."
                  updatedAt={notesUpdatedAt}
                  hideToolbar
                  downloadFilename="speaker-notes.md"
                  versions={notesVersions}
                  activeVersionKey={activeNotesKey}
                  onSelectVersion={onSelectNotesVersion}
                />
              )}

              {effectiveTab === 'presentation' && deckId && (
                <PreviewTab
                  deckId={deckId}
                  workspaceId={workspaceId}
                  deckTitle={deckTitle}
                  deckMode={deckMode ?? null}
                  deckTheme={deckTheme ?? null}
                  deckCurrentVersion={deckCurrentVersion ?? null}
                  onDeckChanged={onDeckChanged}
                  isGenerating={isGenerating}
                  currentActivity={currentActivity}
                  previewRefreshKey={previewRefreshKey}
                  hasReachedPreview={hasReachedPreview}
                  onAdvancePhase={onAdvancePhase}
                  previewTask={previewTask}
                  previewGeneratingNoHTML={previewGeneratingNoHTML}
                  artifacts={artifacts.filter(a => a.filename?.endsWith('.html'))}
                  activeArtifactKey={activeArtifactKey}
                  onSelectArtifact={onSelectArtifact}
                  activeSlideHash={activeSlideHash}
                  onPublishToDroploft={onPublishToDroploft}
                  droploftPublishing={droploftPublishing}
                  artifactSignals={artifactSignals}
                  activeArtifactSignalId={activeArtifactSignalId}
                />
              )}

              {effectiveTab === 'presentation' && !deckId && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  Select a deck to get started
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Briefing floating overlay */}
      {showBriefing && deckId && (
        <div style={{
          position: 'absolute', inset: 0, top: 40, zIndex: 30,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Semi-transparent backdrop */}
          <div
            onClick={() => setShowBriefing(false)}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.15)',
              backdropFilter: 'blur(2px)',
            }}
          />
          {/* Floating panel */}
          <div style={{
            position: 'relative', zIndex: 1,
            margin: 16, flex: 1, minHeight: 0,
            background: 'var(--bg-primary)',
            borderRadius: 0,
            border: '1px solid var(--border)',
            boxShadow: 'none',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Close button */}
            <button
              onClick={() => setShowBriefing(false)}
              style={{
                position: 'absolute', top: 8, right: 8, zIndex: 10,
                width: 28, height: 28, borderRadius: '50%',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              <X size={14} weight="light" />
            </button>
            <ConceptCloud
              deckId={deckId}
              deckTitle={deckTitle}
              audienceInfo={audienceInfo}
              goalInfo={goalInfo}
              keyMessages={keyMessages}
              onClose={() => setShowBriefing(false)}
              contextVersion={contextVersion}
              prefetchedContext={contextItems}
              contextHydratedDeckId={contextHydratedDeckId}
            />
          </div>
        </div>
      )}
      {/* Outer BuildModePicker removed — see generic-viewer Stage 4. */}
      {/* Right-panel artifact overlay (image / pdf / pptx download card).
          Lives at the PreviewPanel level — not inside PreviewTab — because
          PreviewTab only mounts on the 'presentation' tab and doesn't have
          artifactView in its prop scope. Putting the JSX inside PreviewTab
          (where it shipped via #62) compiled to a ReferenceError at runtime
          since artifactView wasn't in PreviewTab's destructure. */}
      {artifactView && deckId && (
        <ArtifactOverlay
          deckId={deckId}
          artifact={artifactView}
          onClose={() => onCloseArtifactView?.()}
        />
      )}
    </div>
  );
}

/**
 * Build a per-element text snapshot of every `.slide` in the deck.
 * Keyed by `<slideId> > <selector path>`. Values are trimmed innerText.
 *
 * Selector path uses `tagName + .firstClass + :nth-of-type(N)` when
 * there are multiple same-tag siblings — that's enough to tell apart
 * the items in a bulleted list without dragging in the full XPath.
 *
 * Elements with empty (post-trim) text are skipped — they can't have
 * a meaningful "before → after" diff and would just bloat the result.
 *
 * Used by the in-place edit flow: snapshot on edit-start, snapshot
 * again on save, diff at element granularity for the [user-edit]
 * agent message.
 */
function snapshotSlideElements(root: Document | HTMLElement): Record<string, string> {
  const snap: Record<string, string> = {};
  const slides = root.querySelectorAll('.slide');
  slides.forEach((slide) => {
    const slideId = (slide as HTMLElement).id;
    if (!slideId) return;
    const all = slide.querySelectorAll('*');
    all.forEach((el) => {
      const text = ((el as HTMLElement).innerText || el.textContent || '').trim();
      if (!text) return;
      // Skip container elements whose text is just the concatenation
      // of children — we want leaf-ish elements that actually carry
      // a user-edited string. Heuristic: element has no child elements,
      // OR all of its children are inline-style elements (br/span/em/
      // strong/a/code) with their own text already captured.
      const childEls = (el as HTMLElement).children;
      if (childEls.length > 0) {
        const allInline = Array.from(childEls).every(c => {
          const t = c.tagName.toLowerCase();
          return ['br', 'span', 'em', 'strong', 'b', 'i', 'a', 'code', 'small', 'sub', 'sup'].includes(t);
        });
        if (!allInline) return;
      }
      const tag = el.tagName.toLowerCase();
      const cls = (el as HTMLElement).className && typeof (el as HTMLElement).className === 'string'
        ? '.' + (el as HTMLElement).className.split(/\s+/).filter(Boolean)[0]
        : '';
      let nth = '';
      const parent = (el as HTMLElement).parentElement;
      if (parent) {
        const sameTagSibs = Array.from(parent.children).filter(c => c.tagName === el.tagName);
        if (sameTagSibs.length > 1) {
          const idx = sameTagSibs.indexOf(el as HTMLElement) + 1;
          nth = `:nth-of-type(${idx})`;
        }
      }
      const path = `${tag}${cls || ''}${nth}`;
      const key = `${slideId} > ${path}`;
      snap[key] = text.slice(0, 400);
    });
  });
  return snap;
}

/** Compute the per-element diff between two snapshots. Each item is
 *  "added", "removed", or "changed". Caller formats for display. */
function diffSlideElements(
  before: Record<string, string>,
  after: Record<string, string>,
): Array<{ key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }> {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: Array<{ key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }> = [];
  keys.forEach((k) => {
    const b = before[k];
    const a = after[k];
    if (b === undefined && a !== undefined) out.push({ key: k, kind: 'added', before: '', after: a });
    else if (a === undefined && b !== undefined) out.push({ key: k, kind: 'removed', before: b, after: '' });
    else if (b !== a) out.push({ key: k, kind: 'changed', before: b || '', after: a || '' });
  });
  return out;
}

/** Preview sub-component — renders iframe + toolbar, no overlays (those are in parent) */
function formatRelativeTime(ts?: string | number | null): string {
  if (!ts) return '';
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function findArtifactSignalForVersion(
  artifactSignals: import('../types').ArtifactSignal[] | undefined,
  version: string,
): import('../types').ArtifactSignal | undefined {
  const target = version.trim();
  if (!target || !artifactSignals?.length) return undefined;
  const encodedDeckGroup = encodeURIComponent(`deck-group:${target}`);
  return artifactSignals.find((artifact) =>
    artifact.id === `deck-group:${target}` ||
    artifact.version_label === target ||
    artifact.display_name === target ||
    artifact.preview_url.includes(`artifact_id=${encodedDeckGroup}`) ||
    artifact.preview_url.includes(`artifact_id=deck-group:${target}`),
  );
}

function appendPreviewQuery(url: string, query: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function serializePreviewDocumentForHtmlExport(doc: Document): string {
  const root = doc.documentElement.cloneNode(true) as HTMLElement;
  root
    .querySelectorAll('#pp-slide-nav, #pp-slide-nav-hover-zone, #pp-slide-nav-style')
    .forEach(node => node.remove());
  root.querySelectorAll('style').forEach(style => {
    const text = style.textContent || '';
    if (text.includes('#pp-slide-nav') || text.includes('#pp-slide-nav-hover-zone')) {
      style.remove();
    }
  });
  return '<!DOCTYPE html>\n' + root.outerHTML;
}

interface SpeakerNotesCacheEntry {
  notes: Record<string, string>;
  loaded: boolean;
  loading: boolean;
  requestKey: number;
}

function sameNotesMap(a: Record<string, string>, b: Record<string, string>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => a[key] === b[key]);
}

/** PowerPoint-style speaker-notes pane (PP-main parity gap C).
 *
 * Mounted under the deck iframe in the Presentation view. Fetches all
 * `slide-NN.note.md` files for the visible deck version once, then
 * switches slides from an in-memory cache. pp-writer writes these
 * files; this is the read-only viewer.
 *
 * Default state is expanded; the toggle is persisted per-deck in
 * localStorage. Missing notes render a placeholder.
 */
function SpeakerNotesPane({
  deckId,
  slideNum,
  version,
  refreshKey,
}: {
  deckId: string;
  slideNum: number | null;
  version?: string;
  refreshKey?: number;
}) {
  const storageKey = `pp:notes-pane:expanded:${deckId}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? true : v === '1';
    } catch { return true; }
  });
  const [cache, setCache] = useState<Record<string, SpeakerNotesCacheEntry>>({});
  const notesVersion = version ?? '';
  const notesCacheKey = `${deckId}:${notesVersion}`;
  const cacheEntry = cache[notesCacheKey];
  const md = slideNum ? cacheEntry?.notes[String(slideNum)] ?? '' : '';
  const loading = !!slideNum && !!cacheEntry?.loading && !cacheEntry.loaded;

  useEffect(() => {
    try { localStorage.setItem(storageKey, expanded ? '1' : '0'); } catch {}
  }, [storageKey, expanded]);

  useEffect(() => {
    if (!version) return;
    const requestKey = refreshKey || 0;
    let cancelled = false;
    const controller = new AbortController();
    setCache(prev => {
      const existing = prev[notesCacheKey];
      if (existing?.requestKey === requestKey && (existing.loading || existing.loaded)) return prev;
      return {
        ...prev,
        [notesCacheKey]: {
          notes: existing?.notes ?? {},
          loaded: existing?.loaded ?? false,
          loading: true,
          requestKey,
        },
      };
    });
    const qs = `?version=${encodeURIComponent(version)}`;
    fetch(`/api/decks/${deckId}/notes${qs}`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (r) => {
        if (cancelled) return null;
        if (!r.ok) throw new Error(`notes fetch ${r.status}`);
        return r.json() as Promise<{ ok?: boolean; data?: { notes?: Record<string, string> } }>;
      })
      .then((body) => {
        if (cancelled) return;
        const nextNotes = body?.data?.notes ?? {};
        setCache(prev => {
          const existing = prev[notesCacheKey];
          const same = existing && sameNotesMap(existing.notes, nextNotes);
          return {
            ...prev,
            [notesCacheKey]: {
              notes: same ? existing.notes : nextNotes,
              loaded: true,
              loading: false,
              requestKey,
            },
          };
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCache(prev => {
          const existing = prev[notesCacheKey];
          return {
            ...prev,
            [notesCacheKey]: {
              notes: existing?.notes ?? {},
              loaded: existing?.loaded ?? false,
              loading: false,
              requestKey,
            },
          };
        });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [deckId, notesCacheKey, refreshKey, version]);

  // Fixed pane height when expanded; body scrolls internally so the pane
  // doesn't grow/shrink with each slide's note length.
  const COLLAPSED_HEIGHT = 32;
  const EXPANDED_HEIGHT = '32vh';
  // Memo BEFORE the early return — hooks must run unconditionally.
  // Explicit options so rendering doesn't depend on ChatPanel's global
  // marked.setOptions being loaded first.
  const html = useMemo(
    () => (md ? (marked.parse(md, { breaks: true, gfm: true }) as string) : ''),
    [md],
  );

  if (!slideNum) return null;

  return (
    <div style={{
      flexShrink: 0,
      borderTop: '1px solid var(--border)',
      background: 'var(--bg-surface)',
      height: expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
      overflow: 'hidden',
      transition: 'height 0.18s ease',
      boxSizing: 'border-box',
    }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px', height: COLLAPSED_HEIGHT, cursor: 'pointer', userSelect: 'none',
          fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
          boxSizing: 'border-box',
        }}
        title={expanded ? 'Collapse speaker notes' : 'Expand speaker notes'}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Notebook size={14} weight="regular" />
          Speaker Notes — slide {slideNum}
        </span>
        <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </div>
      {expanded && (
        <div
          className="pp-speaker-notes-body"
          style={{
            padding: '8px 16px 14px',
            overflowY: 'auto',
            height: `calc(${EXPANDED_HEIGHT} - ${COLLAPSED_HEIGHT}px)`,
            boxSizing: 'border-box',
            fontSize: 'var(--fs-base)',
            lineHeight: 1.55,
            color: 'var(--text-primary)',
          }}
        >
          {loading ? (
            <em style={{ color: 'var(--text-muted)' }}>Loading…</em>
          ) : md ? (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <em style={{ color: 'var(--text-muted)' }}>
              No notes for this slide yet — the agent writes them on the next build.
            </em>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewTab({
  deckId, workspaceId, deckTitle, deckMode, deckTheme, onDeckChanged,
  deckCurrentVersion,
  isGenerating, currentActivity, previewRefreshKey,
  hasReachedPreview, onAdvancePhase, previewTask, previewGeneratingNoHTML,
  artifacts = [], activeArtifactKey, onSelectArtifact,
  activeSlideHash,
  onPublishToDroploft, droploftPublishing = false,
  artifactSignals, activeArtifactSignalId,
}: {
  deckId: string;
  workspaceId?: string | null;
  deckTitle?: string;
  deckMode?: 'html' | 'pptx' | null;
  deckTheme?: 'dify-default' | 'dify-dark' | null;
  deckCurrentVersion?: string | null;
  onDeckChanged?: () => void;
  isGenerating: boolean;
  currentActivity?: string | null;
  previewRefreshKey?: number;
  hasReachedPreview: boolean;
  onAdvancePhase?: (phase: Phase) => void;
  previewTask?: 'create' | 'update' | null;
  previewGeneratingNoHTML?: boolean;
  artifacts?: { filename: string; key: string; updated_at?: string | number }[];
  activeArtifactKey?: string | null;
  onSelectArtifact?: (key: string) => void;
  activeSlideHash?: string;
  onPublishToDroploft?: () => void;
  droploftPublishing?: boolean;
  artifactSignals?: import('../types').ArtifactSignal[];
  activeArtifactSignalId?: string | null;
}) {
  const hasPreview = (previewRefreshKey || 0) > 0 || hasReachedPreview;
  const autoTriggered = useRef(false);
  const [showShare, setShowShare] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Speaker-notes pane gate. Read from /api/health (same public feature
  // surface the LoginPage uses). Defaults off until confirmed true so an
  // admin who disabled feature_speaker_notes_pane never sees a flash.
  const [notesPaneEnabled, setNotesPaneEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/health', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setNotesPaneEnabled(!!d?.features?.speaker_notes_pane); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState | null>(null);
  const [previewDomReady, setPreviewDomReady] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Toolbar dropdown menus. Export folds the downloads + share into one
  // trigger; More folds import/brand/members. This keeps the tab-bar
  // action row compact so it no longer overlaps the version selector /
  // tab title at narrower preview widths.
  const [exportOpen, setExportOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // 1-based slide currently visible in the iframe. Tracked here (lifted
  // out of SlideNav) so the presenter-mode handler can land the
  // audience window on the right slide and broadcast subsequent nav.
  const [currentSlideNum, setCurrentSlideNum] = useState<number>(1);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previewStageRef = useRef<HTMLDivElement>(null);
  const [previewStageSize, setPreviewStageSize] = useState({ width: 0, height: 0 });
  const exportProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot of slide innerText at the moment "Edit" is clicked, keyed
  // by slide id. Diffed against the post-save HTML to produce a richer
  // [user-edit] chat message instead of the generic "please review".
  const editSnapshotRef = useRef<Record<string, string> | null>(null);
  // Anchor-comment mode: when on, clicks inside the deck iframe open a
  // popover anchored to the clicked element so the user can ask the
  // agent for a change without typing the location themselves.
  const [commentMode, setCommentMode] = useState(false);
  const [commentPopover, setCommentPopover] = useState<null | {
    x: number; y: number; anchor: string; elementText: string;
  }>(null);
  const commentTextRef = useRef<HTMLTextAreaElement>(null);
  // Currently outlined element in comment mode — used to remove the
  // highlight class before applying it to a newly clicked element.
  const commentAnchorElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = previewStageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.contentRect;
      if (!rect) return;
      setPreviewStageSize({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const getReadyPreviewDocument = useCallback((): Document | null => {
    const doc = iframeRef.current?.contentDocument ?? null;
    if (!doc) return null;
    if (!doc.querySelector('#pp-deck')) return null;
    if (doc.querySelectorAll('.slide').length === 0) return null;
    return doc;
  }, []);

  const markPreviewDomReady = useCallback(() => {
    let attempts = 0;
    const check = () => {
      const ready = !!getReadyPreviewDocument();
      setPreviewDomReady(ready);
      if (!ready && attempts < 20) {
        attempts += 1;
        window.setTimeout(check, 250);
      }
    };
    check();
  }, [getReadyPreviewDocument]);

  // Treat null mode as html (legacy decks).
  const effectiveMode: 'html' | 'pptx' = deckMode === 'pptx' ? 'pptx' : 'html';

  // safeAdvancePhase: pass-through. Mode picker fully retired in
  // Stage 4 of #87 alignment — agent picks per-turn from user intent.
  const safeAdvancePhase = useCallback((target: Phase) => {
    onAdvancePhase?.(target);
  }, [onAdvancePhase]);

  // ── Deck versions from API ──
  const [apiVersions, setApiVersions] = useState<{ key: string; title: string; slides: number; created_at: number; updated_at: number }[]>([]);
  const [currentVersion, setCurrentVersion] = useState(deckCurrentVersion ?? '');

  useEffect(() => {
    if (deckCurrentVersion) setCurrentVersion(deckCurrentVersion);
  }, [deckCurrentVersion]);

  useEffect(() => {
    if (!deckId) return;
    apiFetch(`/api/decks/${deckId}/versions?_t=${Date.now()}`, { cache: 'no-store' })
      .then(async r => {
        const d = await r.json().catch(() => null);
        if (d?.ok && d.data) {
          setApiVersions(d.data.versions || []);
          setCurrentVersion(d.data.current || '');
        }
      })
      .catch(() => {}); // Passive list; AuthExpiredError already surfaced by apiFetch, network errors stay quiet
  }, [deckId, previewRefreshKey]);

  const hasMultipleVersions = apiVersions.length > 1;

  // Filter to assembled/monolithic decks (not individual slides or manifest)
  const assembledArtifacts = artifacts.filter(a =>
    a.filename === 'assembled.html' || (!a.filename?.startsWith('slide-') && !a.filename?.startsWith('manifest'))
  );
  // Priority: explicit selection → match current DB version → first assembled → first artifact
  const activeArtifact =
    (activeArtifactKey && assembledArtifacts.find(a => a.key === activeArtifactKey))
    || (currentVersion && assembledArtifacts.find(a => a.key.includes(`/${currentVersion}/`)))
    || assembledArtifacts[0]
    || artifacts[0];

  // Sync: when currentVersion loads from API and no explicit selection, select the matching artifact
  useEffect(() => {
    if (currentVersion && !activeArtifactKey && assembledArtifacts.length > 0) {
      const match = assembledArtifacts.find(a => a.key.includes(`/${currentVersion}/`));
      if (match) onSelectArtifact?.(match.key);
    }
  }, [currentVersion, assembledArtifacts.length]);

  // Derive deck folder from active artifact key
  const deckFolder = activeArtifact?.key?.split('/').slice(1, -1).join('/') || null;

  const previewKey = activeArtifact?.key;
  // For CF-native decks (no artifacts table) we use the version
  // name directly; for legacy artifact-based decks we keep the
  // existing key path. Either way, both end up mounting a fresh
  // iframe when the version flips.
  const effectivePreviewKey = currentVersion || previewKey || '';
  const keyParam = effectivePreviewKey
    ? `&key=${encodeURIComponent(effectivePreviewKey)}`
    : '';
  const editParam = editMode ? '&edit=1' : '';
  // Generic artifact viewer (PP main #87 alignment). When signals has
  // an active artifact, build the iframe URL from its preview_url
  // (which already encodes the artifact_id resolution server-side).
  // Falls back to the legacy keyParam construction otherwise so
  // pre-#78 decks and signals-less responses still preview.
  const activeArtifactSignal =
    findArtifactSignalForVersion(artifactSignals, currentVersion) ||
    (currentVersion ? undefined : artifactSignals?.find((a) => a.id === activeArtifactSignalId));
  const previewBaseUrl = activeArtifactSignal
    ? appendPreviewQuery(activeArtifactSignal.preview_url, `_t=${previewRefreshKey || 0}`)
    : `/api/decks/${deckId}/preview?_t=${previewRefreshKey || 0}${keyParam}`;
  const previewDocumentUrl = `${previewBaseUrl}${editParam}`;

  useEffect(() => {
    setPreviewDomReady(false);
  }, [previewDocumentUrl]);

  useEffect(() => {
    if (!activeSlideHash || !previewDomReady) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      if (win.location.hash !== activeSlideHash) {
        win.location.hash = activeSlideHash;
      }
    } catch {
      /* same-origin iframe navigation only */
    }
  }, [activeSlideHash, previewDomReady, previewDocumentUrl]);

  // Use custom event for edit-saved notification (avoids stale closure issues)

  // Listen for postMessage from editor iframe — save full assembled HTML
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || e.data.type !== 'pp:html') return;
      const html = e.data.html;
      if (!html || !deckId) return;
      setSaving(true);
      apiFetch(`/api/decks/${deckId}/preview`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html,
          deckFolder,
          editedSlide: typeof e.data.editedSlide === 'number'
            ? e.data.editedSlide
            : null,
          editedSlideTitle: e.data.editedSlideTitle ?? null,
          field: 'body',
        }),
      })
        .then(async r => {
          const data = await r.json().catch(() => ({ ok: false, error: 'Invalid server response' }));
          if (!data.ok) {
            notifyError(data.error ? `Save failed: ${data.error}` : 'Save failed — please try again');
            return;
          }
          setEditMode(false);
          // Diff the pre-edit snapshot against the saved HTML so the
          // chat message lists *what actually changed* instead of a
          // generic "please review". Falls back to the slide-info hint
          // if the snapshot is missing or no text diffs are detected.
          const before = editSnapshotRef.current || {};
          let after: Record<string, string> = {};
          try {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            after = snapshotSlideElements(parsed);
          } catch {}
          const diffs = diffSlideElements(before, after);
          editSnapshotRef.current = null;
          const slideInfo = e.data.editedSlide
            ? `slide ${e.data.editedSlide}${e.data.editedSlideTitle ? ` "${e.data.editedSlideTitle}"` : ''}`
            : 'the presentation';
          const MAX_LIST = 15;
          const shown = diffs.slice(0, MAX_LIST);
          const overflow = diffs.length - shown.length;
          const formatLine = (d: { key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }): string => {
            // key is "slide-03 > h1.title:nth-of-type(2)" — split for
            // readability in the agent message.
            const [slidePart, ...selParts] = d.key.split(' > ');
            const sel = selParts.join(' > ');
            if (d.kind === 'added')   return `- ${slidePart} -> ${sel}: (added) ${JSON.stringify(d.after.slice(0, 200))}`;
            if (d.kind === 'removed') return `- ${slidePart} -> ${sel}: (removed) ${JSON.stringify(d.before.slice(0, 200))}`;
            return `- ${slidePart} -> ${sel}: ${JSON.stringify(d.before.slice(0, 200))} -> ${JSON.stringify(d.after.slice(0, 200))}`;
          };
          let what: string;
          if (diffs.length === 0) {
            what = `[user-edit] Saved ${slideInfo} (no visible text changes detected). Please review.`;
          } else {
            const lines = shown.map(formatLine).join('\n');
            const tail = overflow > 0 ? `\n+ ${overflow} more` : '';
            what = `[user-edit] User edited ${diffs.length} element(s):\n${lines}${tail}\n\nThis captures the new text but not the user's intent. Ask the user what they want next — should this be finalized, propagated to other slides, or restyled? Do not assume; clarify first.`;
          }
          window.dispatchEvent(
            new CustomEvent('pp:edit-saved', {
              detail: {
                what,
                // Carry the slide number so the App-level listener can
                // anchor the iframe at that slide before it remounts
                // (editMode flip changes the iframe key). Without this
                // anchor the preview always jumps to slide 1 after save.
                slideNum:
                  typeof e.data.editedSlide === 'number'
                    ? e.data.editedSlide
                    : null,
              },
            }),
          );
        })
        .catch(err => {
          if (err instanceof AuthExpiredError) return; // Modal already shown
          notifyError(`Save failed: ${err?.message || 'network error'}`);
        })
        .finally(() => setSaving(false));
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [deckId, deckFolder]);

  // Auto-trigger builder when preview tab is shown but no preview exists.
  // When the deck's mode hasn't been picked, we still auto-trigger but
  // route through safeAdvancePhase, which opens the BuildModePicker
  // instead of POSTing /:id/phase directly.
  useEffect(() => {
    if (!hasPreview && !isGenerating && !autoTriggered.current && onAdvancePhase) {
      autoTriggered.current = true;
      safeAdvancePhase('preview');
    }
  }, [hasPreview, isGenerating, onAdvancePhase, safeAdvancePhase]);

  useEffect(() => {
    autoTriggered.current = false;
  }, [deckId]);

  // ── Slide-change subscription ──────────────────────────────────────
  // The embedded Liquid Glass dock (slide-nav-source.ts) fires
  // `pp:slide-changed` to window.parent on click, IO scroll-detect,
  // keyboard, and initial mount. We mirror its 1-based slide number
  // into currentSlideNum so the presenter-mode BroadcastChannel and
  // "anchor at slide" hash on edit-mode entry both stay in sync.
  // Validates payload strictly; iframe origin can be same-host
  // ('/preview') or local file:// (exported deck) so we accept '*'.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type !== 'pp:slide-changed') return;
      const n = Number(d.slide);
      if (!Number.isFinite(n) || n < 1) return;
      setCurrentSlideNum(n);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Presenter-mode sync ────────────────────────────────────────────
  // When the user opens the deck on a secondary monitor via the
  // Fullscreen action, the audience window subscribes to
  // `pp-deck-{deckId}` and follows slide changes. We always publish
  // the current slide here so the audience is ready before we even
  // know it exists — an empty channel is a no-op. We also listen for
  // `nav` messages from the audience (its own ArrowLeft/Right
  // keystrokes) and synthesize the same navigation in the main
  // window's iframe so both stay in sync.
  useEffect(() => {
    if (!deckId || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(`pp-deck-${deckId}`);
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'nav') {
        const iframe = iframeRef.current;
        const win = iframe?.contentWindow;
        if (!win) return;
        const key = d.direction === 'prev' ? 'ArrowLeft' : 'ArrowRight';
        try {
          win.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
        } catch {}
        // Also broadcast through window so SlideNav's listener picks it up.
        try {
          window.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
        } catch {}
      } else if (d.type === 'slide' && d.source === 'presenter') {
        const n = Number(d.slide);
        if (!Number.isFinite(n) || n < 1) return;
        setCurrentSlideNum(n);
        const iframe = iframeRef.current;
        const win = iframe?.contentWindow;
        if (!win) return;
        try {
          const hash = `#slide-${String(n).padStart(2, '0')}`;
          if (win.location.hash !== hash) win.location.hash = hash;
        } catch {}
      }
    };
    ch.addEventListener('message', onMsg);
    return () => { ch.removeEventListener('message', onMsg); ch.close(); };
  }, [deckId]);

  useEffect(() => {
    if (!deckId || typeof BroadcastChannel === 'undefined') return;
    if (currentSlideNum == null || currentSlideNum < 1) return;
    const ch = new BroadcastChannel(`pp-deck-${deckId}`);
    try { ch.postMessage({ type: 'slide', slide: currentSlideNum }); } catch {}
    ch.close();
  }, [deckId, currentSlideNum]);

  // Presenter fullscreen opens a dedicated presentation surface, close
  // to the PP EKS projection flow: black shell, full-viewport deck
  // iframe, and a click-to-enter-OS-fullscreen overlay. The workspace
  // iframe remains mounted and synced through BroadcastChannel.
  const handleFullscreen = useCallback(async () => {
    if (!deckId) return;
    const padSlide = (n: number) => String(n).padStart(2, '0');
    const startHash = currentSlideNum && currentSlideNum > 0
      ? `#slide-${padSlide(currentSlideNum)}`
      : '';
    const channelName = `pp-deck-${deckId}`;
    const presenterPreviewUrl = appendPreviewQuery(previewBaseUrl, 'presenter=1');
    const presenterUrl = `${presenterPreviewUrl}${startHash}`;
    const presenter = window.open('', `pp-presenter-${deckId}`, 'popup=yes,width=1280,height=800');

    if (!presenter) {
      notifyError('Fullscreen window was blocked. Please allow popups for PitchPilot and try again.');
      return;
    }

    const safeTitle = (deckTitle || 'Deck').replace(/[<>"']/g, '');
    const frameSrc = escapeHtmlAttr(presenterUrl);
    presenter.document.open();
    presenter.document.write(`<!DOCTYPE html>
<html><head><title>Presenting - ${safeTitle}</title>
<meta charset="utf-8">
<style>
  html,body{margin:0;height:100%;overflow:hidden;background:#000;font-family:system-ui,sans-serif}
  iframe{width:100%;height:100%;border:0;display:block;background:#000}
  #fs-overlay{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(0,0,0,0.48);transition:opacity .22s ease}
  #fs-overlay.hidden{opacity:0;pointer-events:none}
  #fs-overlay .label{color:#fff;font:500 18px/1.4 system-ui,sans-serif;letter-spacing:0;padding:10px 22px;border:1px solid rgba(255,255,255,0.38);border-radius:10px;background:rgba(0,0,0,0.36);backdrop-filter:blur(6px)}
</style>
</head><body>
<iframe id="d" src="${frameSrc}" allowfullscreen></iframe>
<div id="fs-overlay"><span class="label">Click to present fullscreen</span></div>
<script>
  (function(){
    var deckFrame = document.getElementById('d');
    var overlay = document.getElementById('fs-overlay');
    var ch = typeof BroadcastChannel === 'function' ? new BroadcastChannel(${JSON.stringify(channelName)}) : null;
    var lastSlide = ${currentSlideNum || 1};
    function goTo(slide){
      if (!slide) return;
      lastSlide = slide;
      try {
        var win = deckFrame.contentWindow;
        if (win) {
          var hash = '#slide-' + String(slide).padStart(2, '0');
          if (win.location.hash !== hash) win.location.hash = hash;
        }
      } catch (e) {}
    }
    if (ch) {
      ch.onmessage = function(e){
        if (e.data && e.data.type === 'slide' && typeof e.data.slide === 'number') goTo(e.data.slide);
      };
    }
    overlay.addEventListener('click', function(){
      try {
        var p = document.documentElement.requestFullscreen();
        if (p && p.catch) p.catch(function(){});
      } catch (e) {}
      overlay.classList.add('hidden');
      setTimeout(function(){ overlay && overlay.parentNode && overlay.parentNode.removeChild(overlay); }, 350);
    });
    if (ch) ch.postMessage({type:'presenter-open'});
    window.addEventListener('message', function(e){
      if (!e.data || e.data.type !== 'pp:slide-changed') return;
      var slide = Number(e.data.slide);
      if (!Number.isFinite(slide) || slide < 1) return;
      lastSlide = slide;
      if (ch) ch.postMessage({type:'slide', source:'presenter', slide:slide});
    });
    window.addEventListener('beforeunload', function(){ try{ if (ch) { ch.postMessage({type:'presenter-close'}); ch.close(); } }catch(e){} });
    window.addEventListener('keydown', function(e){
      if (!ch) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'PageDown') ch.postMessage({type:'nav', direction:'next'});
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') ch.postMessage({type:'nav', direction:'prev'});
    });
  })();
</script>
</body></html>`);
    presenter.document.close();
    try { presenter.focus(); } catch {}

    const screenApi: any = (window as any).getScreenDetails;
    if (typeof screenApi === 'function') {
      try {
        const details = await screenApi.call(window);
        const screens: any[] = details?.screens || [];
        const currentLabel = details?.currentScreen?.label;
        const other = screens.find(s => s.label !== currentLabel)
          || (screens.length > 1 ? screens.find(s => s !== details.currentScreen) : null);
        if (other && !presenter.closed) {
          const left = other.availLeft ?? other.left ?? 0;
          const top = other.availTop ?? other.top ?? 0;
          const width = other.availWidth ?? other.width ?? 1280;
          const height = other.availHeight ?? other.height ?? 800;
          try { presenter.moveTo(left, top); } catch {}
          try { presenter.resizeTo(width, height); } catch {}
          try { presenter.focus(); } catch {}
        }
      } catch {}
    }
  }, [deckId, deckTitle, currentSlideNum, previewBaseUrl]);

  // Anchor-comment mode: install a capture-phase click handler inside
  // the deck iframe so the next click opens a popover instead of
  // navigating. Records the clicked element's slide id + tag/class
  // selector + a short text preview, then positions the popover in
  // main-window coords via iframe.getBoundingClientRect() + element
  // rect math (so it stays anchored even with the chat sidebar).
  useEffect(() => {
    if (!commentMode) { setCommentPopover(null); return; }
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;
    const styleEl = doc.createElement('style');
    // Crosshair cursor (whole tree) + outline on the clicked anchor
    // so the user can visually confirm which element will receive
    // their comment. Outline-offset prevents clipping the element's
    // own border/shadow.
    styleEl.textContent = [
      '*, *::before, *::after { cursor: crosshair !important; }',
      '.pp-comment-anchor {',
      '  outline: 2px solid var(--brand, #2684ff) !important;',
      '  outline-offset: 4px !important;',
      '  transition: outline 0.12s ease !important;',
      '}',
    ].join('\n');
    doc.head?.appendChild(styleEl);
    const clearAnchorHighlight = () => {
      const prev = commentAnchorElRef.current;
      if (prev) {
        try { prev.classList.remove('pp-comment-anchor'); } catch {}
        commentAnchorElRef.current = null;
      }
    };
    const onClick = (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      let slideEl: HTMLElement | null = target;
      while (slideEl && !slideEl.classList?.contains('slide')) slideEl = slideEl.parentElement;
      const slideId = slideEl?.id || '';
      const cls = (target.className && typeof target.className === 'string')
        ? '.' + target.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')
        : '';
      const tagDesc = target.tagName.toLowerCase() + cls;
      const textPreview = ((target as HTMLElement).innerText || target.textContent || '').trim().slice(0, 120);
      // Highlight the new target; drop the previous so we never
      // leave more than one outline behind.
      clearAnchorHighlight();
      try { target.classList.add('pp-comment-anchor'); } catch {}
      commentAnchorElRef.current = target;
      const elemRect = target.getBoundingClientRect();
      const iframeRect = iframe!.getBoundingClientRect();
      // Popup dimensions match the JSX below (maxWidth 360, ~200px tall).
      // Clamp so the popup stays inside the viewport — without this, a
      // click near the right or bottom edge of the preview pane pushes
      // the popup off-screen (aligns PP main #122).
      const POPUP_W = 360;
      const POPUP_H = 200;
      const PAD = 8;
      let x = iframeRect.left + elemRect.left;
      let y = iframeRect.top + elemRect.bottom + 8;
      x = Math.max(PAD, Math.min(x, window.innerWidth - POPUP_W - PAD));
      if (y + POPUP_H + PAD > window.innerHeight) {
        const flipped = iframeRect.top + elemRect.top - POPUP_H - 8;
        y = Math.max(PAD, flipped);
      }
      setCommentPopover({
        x, y,
        anchor: slideId ? `${slideId} → ${tagDesc}` : tagDesc,
        elementText: textPreview,
      });
      setTimeout(() => commentTextRef.current?.focus(), 30);
    };
    doc.addEventListener('click', onClick, true);
    return () => {
      try { doc.removeEventListener('click', onClick, true); } catch {}
      try { styleEl.remove(); } catch {}
      clearAnchorHighlight();
    };
  }, [commentMode]);

  // Dismiss the comment popover and drop the outline highlight, but
  // keep comment mode itself on — the user may want to pick a
  // different element right after cancelling. Toggling comment mode
  // off (via the toolbar) tears down the click listener separately.
  const closeCommentPopover = useCallback(() => {
    const prev = commentAnchorElRef.current;
    if (prev) {
      try { prev.classList.remove('pp-comment-anchor'); } catch {}
      commentAnchorElRef.current = null;
    }
    setCommentPopover(null);
  }, []);

  const sendComment = useCallback(() => {
    const text = commentTextRef.current?.value?.trim();
    if (!text || !commentPopover) return;
    const what = `[user-comment] anchor=${commentPopover.anchor}; element_text=${JSON.stringify(commentPopover.elementText)}; request=${JSON.stringify(text)}\n\nThe user clicked this element and asked for the change above. The anchor identifies *where* and the request states *what* — act directly (no need to re-confirm intent).`;
    window.dispatchEvent(new CustomEvent('pp:edit-saved', { detail: { what } }));
    closeCommentPopover();
    setCommentMode(false);
  }, [commentPopover, closeCommentPopover]);

  const clearExportProgressTimer = useCallback(() => {
    if (exportProgressTimerRef.current) {
      clearTimeout(exportProgressTimerRef.current);
      exportProgressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearExportProgressTimer(), [clearExportProgressTimer]);

  const beginExportProgress = useCallback((kind: ExportKind, label: string) => {
    clearExportProgressTimer();
    setSelectorOpen(false);
    setMoreOpen(false);
    setExportProgress({ kind, label, progress: 4, status: 'running' });
  }, [clearExportProgressTimer]);

  const updateExportProgress = useCallback((kind: ExportKind, label: string, progress: number) => {
    setExportProgress({ kind, label, progress, status: 'running' });
  }, []);

  const settleExportProgress = useCallback((
    kind: ExportKind,
    label: string,
    status: 'complete' | 'error',
  ) => {
    clearExportProgressTimer();
    setExportProgress({ kind, label, progress: 100, status });
    exportProgressTimerRef.current = setTimeout(() => {
      setExportProgress(null);
      exportProgressTimerRef.current = null;
    }, status === 'complete' ? 1400 : 2600);
  }, [clearExportProgressTimer]);

  const exportBusy = downloading;
  const previewMutationLocked = exportBusy;

  const previewFrameSize = useMemo(() => {
    const availableWidth = Math.max(0, previewStageSize.width);
    const availableHeight = Math.max(0, previewStageSize.height);
    if (!availableWidth || !availableHeight) {
      return { width: '100%', height: '100%' };
    }
    const aspect = 16 / 9;
    let width = availableWidth;
    let height = width / aspect;
    if (height > availableHeight) {
      height = availableHeight;
      width = height * aspect;
    }
    return {
      width: `${Math.floor(width)}px`,
      height: `${Math.floor(height)}px`,
    };
  }, [previewStageSize]);

  // No preview yet and not building — show prompt
  if (!hasPreview && !previewTask) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <Sparkle size={20} weight="light" style={{ color: 'var(--text-muted)', margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 300, marginBottom: 8, color: 'var(--text-primary)' }}>
            Generate Preview
          </h2>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5 }}>
            Click below to generate a presentation from your current brief.
          </p>
          <button
            onClick={() => safeAdvancePhase('preview')}
            style={{
              padding: '8px 24px', fontSize: 'var(--fs-sm)', fontWeight: 400,
              background: 'var(--text-primary)', color: 'var(--bg-primary)', border: 'none', borderRadius: 4,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Sparkle size={12} weight="light" />
            Start Building
          </button>
        </div>
        {/* BuildModePicker removed — Stage 4 of #87 alignment. */}
      </div>
    );
  }

  // Building in progress but no HTML yet. Do not mount the iframe
  // against /preview until the agent has produced a real deck artifact;
  // otherwise the server can legitimately return the empty-preview page,
  // which reads as a blank white canvas inside the viewer.
  if (previewTask === 'create' && (previewGeneratingNoHTML || artifacts.length === 0)) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-primary)', position: 'relative', overflow: 'hidden',
      }}>
        {/* Concentric circle zen fire */}
        {[60, 44, 28].map((size, i) => (
          <div key={i} style={{
            width: size, height: size, borderRadius: '50%',
            border: `2px solid rgba(245,158,11,${0.6 - i * 0.15})`,
            position: 'absolute',
            animation: `zenGlow 3s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
          }} />
        ))}
        <div style={{
          position: 'relative', zIndex: 1, marginTop: 140,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          maxWidth: 420, padding: '0 24px', textAlign: 'center',
        }}>
          <p style={{
            color: 'var(--text-muted)', margin: 0,
            fontSize: 'var(--fs-sm)', fontWeight: 300, letterSpacing: '0.05em',
          }}>
            Crafting your presentation...
          </p>
          {currentActivity ? (
            <p style={{
              color: 'var(--text-secondary)', margin: 0,
              fontSize: 'var(--fs-2xs)', lineHeight: 1.5,
              opacity: 0.85,
            }}>
              {currentActivity}
            </p>
          ) : null}
        </div>
        <style>{`
          @keyframes zenGlow {
            0%, 100% { transform: scale(1); opacity: 0.4; }
            50% { transform: scale(1.2); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  const btnStyle: React.CSSProperties = {
    padding: '4px 10px', background: 'none', border: '1px solid var(--border)',
    borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    color: 'var(--text-secondary)', transition: 'all 0.15s',
    fontSize: 'var(--fs-2xs)', fontWeight: 400, fontFamily: 'inherit',
  };
  // Dropdown menu container + row styles, shared by Export / More.
  const menuStyle: React.CSSProperties = {
    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
    background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 10, minWidth: 180, padding: 4,
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    display: 'flex', flexDirection: 'column', gap: 2,
  };
  const menuItemStyle: React.CSSProperties = {
    padding: '7px 10px', background: 'none', border: 'none', borderRadius: 6,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
    color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', fontWeight: 400,
    fontFamily: 'inherit', width: '100%', textAlign: 'left', textDecoration: 'none',
    whiteSpace: 'nowrap',
  };
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar — collapsed, actions float into tab bar */}
      <div style={{
        height: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: 0, background: 'transparent', flexShrink: 0,
        position: 'relative', overflow: 'visible', zIndex: 50,
      }}>
        {/* Actions — positioned to float up into tab bar */}
        <div className="pp-tab-actions" style={{ position: 'absolute', top: -32, right: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          {apiVersions.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  if (previewMutationLocked) return;
                  setSelectorOpen(!selectorOpen);
                }}
                title="Versions"
                style={{
                  ...btnStyle,
                  padding: '4px 10px',
                }}
              >
                {currentVersion ? currentVersion.replace(/-\d+$/, '') : 'Versions'}
                {hasMultipleVersions && ' ▾'}
              </button>
              {selectorOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
                  background: 'var(--bg-primary)', border: '1px solid var(--border)',
                  borderRadius: 10, minWidth: 240, maxHeight: 240, overflowY: 'auto',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                }}>
                  {apiVersions.map(v => {
                    const isActive = v.key === currentVersion;
                    const subtitle = v.title && v.title !== v.key ? v.title : null;
                    return (
                      <div
                        key={v.key}
                        style={{
                          display: 'flex', alignItems: 'center',
                          padding: '8px 10px 8px 14px',
                          cursor: previewMutationLocked ? 'not-allowed' : 'pointer',
                          opacity: previewMutationLocked ? 0.55 : 1,
                          background: isActive ? 'var(--bg-surface)' : 'transparent',
                          borderRadius: 6, margin: 4, gap: 8,
                        }}
                        onMouseOver={e => { if (!isActive && !previewMutationLocked) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                        onMouseOut={e => { if (!isActive && !previewMutationLocked) e.currentTarget.style.background = isActive ? 'var(--bg-surface)' : 'transparent'; }}
                      >
                        <div
                          onClick={() => {
                            if (previewMutationLocked) return;
                            apiFetch(`/api/decks/${deckId}/versions/current`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ name: v.key }),
                            })
                              .then(async r => {
                                const d = await r.json().catch(() => ({ ok: false, error: 'Invalid server response' }));
                                if (!d.ok) {
                                  notifyError(d.error ? `Switch version failed: ${d.error}` : 'Switch version failed');
                                  return;
                                }
                                setCurrentVersion(d.data.current);
                                setPreviewDomReady(false);
                                const match = assembledArtifacts.find(a => a.key.includes(`/${v.key}/`));
                                if (match) onSelectArtifact?.(match.key);
                                // Re-fetch the deck so signals.active_artifact_id
                                // (now keyed to current_version server-side)
                                // propagates → the generic-viewer preview src
                                // follows the switch instead of staying on the
                                // newest version.
                                onDeckChanged?.();
                              })
                              .catch(err => {
                                if (err instanceof AuthExpiredError) return;
                                notifyError(`Switch version failed: ${err?.message || 'network error'}`);
                              });
                            setSelectorOpen(false);
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span style={{
                              fontSize: 'var(--fs-sm)', fontWeight: isActive ? 600 : 500,
                              color: 'var(--text-primary)', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            }}>
                              {v.key}
                            </span>
                            {subtitle && (
                              <span style={{
                                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {subtitle}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                            {v.slides > 0 ? `${v.slides} slides · ` : ''}{formatRelativeTime(v.updated_at || v.created_at)}
                          </div>
                        </div>
                        <a
                          href={exportPptxUrl(deckId, v.key)}
                          download
                          title={`Download ${v.key} as .pptx`}
                          onClick={e => e.stopPropagation()}
                          style={{
                            color: 'var(--text-muted)',
                            display: 'inline-flex', alignItems: 'center',
                            padding: 6, borderRadius: 4, textDecoration: 'none',
                            flexShrink: 0,
                          }}
                          onMouseOver={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                          onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                        >
                          <DownloadSimple size={13} weight="light" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {editMode ? (
            <>
              {/* Save */}
              <button
                onClick={() => {
                  if (saving || previewMutationLocked) return;
                  iframeRef.current?.contentWindow?.postMessage({ type: 'pp:save' }, '*');
                }}
                disabled={saving}
                title="Save changes"
                style={{
                  ...btnStyle,
                  background: '#0033FF',
                  borderColor: '#0033FF',
                  color: '#fff',
                  opacity: saving ? 0.5 : 1,
                  fontWeight: 600,
                }}
              >
                <FloppyDisk size={13} weight="bold" />
                <span style={{ fontSize: 'var(--fs-2xs)', marginLeft: 4 }}>
                  {saving ? 'Saving...' : 'Save'}
                </span>
              </button>
              {/* Cancel */}
              <button
                onClick={() => {
                  if (previewMutationLocked) return;
                  iframeRef.current?.contentWindow?.postMessage({ type: 'pp:exitEdit' }, '*');
                  setEditMode(false);
                }}
                title="Cancel editing"
                style={{
                  ...btnStyle,
                  color: 'var(--text-secondary)',
                }}
              >
                <X size={13} weight="bold" />
                <span style={{ fontSize: 'var(--fs-2xs)', marginLeft: 4 }}>Cancel</span>
              </button>
            </>
          ) : (
            <>
              {/* Edit — HTML mode only. PPTX mode users edit in PowerPoint after download. */}
              {hasPreview && effectiveMode === 'html' && (
                <button
                  onClick={() => {
                    if (previewMutationLocked) return;
                    // Snapshot pre-edit slide text so we can diff after
                    // save and surface what actually changed in chat,
                    // at element granularity (heading vs bullet etc.).
                    try {
                      const doc = iframeRef.current?.contentDocument;
                      if (doc) editSnapshotRef.current = snapshotSlideElements(doc);
                    } catch {}
                    setEditMode(true);
                  }}
                  title="Edit slides"
                  style={{
                    ...btnStyle,
                  }}
                >
                  <PencilSimple size={13} weight="light" />
                  <span>Edit</span>
                </button>
              )}
              {/* Comment — HTML mode only. Click-to-anchor: enables a
                  click capture inside the iframe; the next click opens
                  a popover anchored to that element so the user can
                  send a [user-comment] chat message with location +
                  intent baked in. */}
              {hasPreview && effectiveMode === 'html' && (
                <button
                  onClick={() => {
                    if (previewMutationLocked) return;
                    setCommentMode(v => !v);
                  }}
                  title={commentMode ? 'Cancel comment mode' : 'Click an element to comment on it'}
                  style={commentMode
                    ? { ...btnStyle, background: 'rgba(38,132,255,0.12)', borderColor: 'rgba(38,132,255,0.3)' }
                    : btnStyle}
                >
                  <ChatCircle size={13} weight="light" />
                  <span>{commentMode ? 'Cancel' : 'Comment'}</span>
                </button>
              )}
              {/* Export ▾ — downloads + share folded into one menu so the
                  tab-bar row stays compact (it was 6+ flat buttons that
                  overlapped the version selector / tab title at narrow
                  widths). */}
              {(hasPreview || currentVersion) && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      if (exportBusy) return;
                      setExportOpen(o => !o);
                      setMoreOpen(false);
                    }}
                    title="Export & share"
                    style={exportOpen
                      ? { ...btnStyle, background: 'var(--bg-surface)' }
                      : btnStyle}
                  >
                    <DownloadSimple size={13} weight="light" />
                    <span>Export</span>
                  </button>
                  {exportOpen && (
                    <div style={menuStyle}>
                      {/* Download HTML — client-side dump of the iframe DOM. */}
	                      {hasPreview && effectiveMode === 'html' && (
	                        <button
	                          disabled={exportBusy || !previewDomReady}
	                          onClick={() => {
	                            if (exportBusy) return;
	                            setDownloading(true);
	                            setExportOpen(false);
	                            beginExportProgress('html', 'Preparing HTML export');
	                            try {
	                              updateExportProgress('html', 'Reading browser preview', 35);
	                              const doc = getReadyPreviewDocument();
	                              if (!doc) {
	                                notifyError('Preview is still loading — wait for the slides to appear, then try again.');
	                                settleExportProgress('html', 'HTML export failed', 'error');
	                                return;
	                              }
	                              const html = serializePreviewDocumentForHtmlExport(doc);
	                              updateExportProgress('html', 'Packaging HTML file', 70);
	                              const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
	                              const title = (deckTitle || 'presentation').replace(/[^a-zA-Z0-9._-]/g, '_');
	                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${title}.html`;
                              document.body.appendChild(a);
	                              a.click();
	                              a.remove();
	                              setTimeout(() => URL.revokeObjectURL(url), 0);
	                              settleExportProgress('html', 'HTML download started', 'complete');
	                            } catch (err) {
	                              notifyError(`Download failed: ${(err as Error)?.message || 'unknown error'}`);
	                              settleExportProgress('html', 'HTML export failed', 'error');
	                            } finally {
	                              setDownloading(false);
	                            }
	                          }}
	                          title="Download HTML"
	                          style={{ ...menuItemStyle, opacity: (exportBusy || !previewDomReady) ? 0.5 : 1 }}
	                        >
	                          <DownloadSimple size={14} weight="light" />
                          <span>{downloading ? 'Downloading…' : previewDomReady ? 'Download HTML' : 'Loading preview…'}</span>
                        </button>
                      )}
                      {/* Publish to Droploft — only when a connection exists. */}
                      {onPublishToDroploft && hasPreview && effectiveMode === 'html' && (
                        <button
                          disabled={droploftPublishing}
                          onClick={() => {
                            if (droploftPublishing) return;
                            setExportOpen(false);
                            onPublishToDroploft();
                          }}
                          title="Publish HTML deck to Droploft"
                          style={{ ...menuItemStyle, opacity: droploftPublishing ? 0.5 : 1 }}
                        >
                          <UploadSimple size={14} weight="light" />
                          <span>{droploftPublishing ? 'Publishing…' : 'Publish to Droploft'}</span>
                        </button>
                      )}
                      {/* Share */}
                      <button onClick={() => { setExportOpen(false); setShowShare(true); }} title="Share" style={menuItemStyle}>
                        <ShareNetwork size={14} weight="light" />
                        <span>Share link</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
              {/* More ⋯ — deck utilities (import reference material, brand
                  profile, members) folded out of the main row. */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => { setMoreOpen(o => !o); setExportOpen(false); }}
                  title="More"
                  style={moreOpen ? { ...btnStyle, background: 'var(--bg-surface)' } : btnStyle}
                >
                  <span style={{ fontSize: 'var(--fs-md)', lineHeight: 1, letterSpacing: 1 }}>⋯</span>
                </button>
                {moreOpen && (
                  <div style={menuStyle}>
                    {/* Members — invite / role / transfer. */}
                    <button onClick={() => { setMoreOpen(false); setShowMembers(true); }} title="Deck members" style={menuItemStyle}>
                      <Users size={14} weight="light" />
                      <span>Members</span>
                    </button>
                  </div>
                )}
              </div>
              {/* Fullscreen opens a clean presenter surface instead of
                  enlarging the embedded workspace iframe. */}
              <button
                onClick={handleFullscreen}
                title="Present fullscreen"
                style={btnStyle}
              >
                <ArrowsOut size={13} weight="light" />
              </button>
            </>
          )}
        </div>

      </div>

      {/* Close dropdown on outside click — must be below dropdown z-index */}
      {selectorOpen && (
        <div
          onClick={() => setSelectorOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 4 }}
        />
      )}
      {(exportOpen || moreOpen) && (
        <div
          onClick={() => { setExportOpen(false); setMoreOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 49 }}
        />
      )}

      <div
        ref={previewStageRef}
        style={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'var(--bg-preview)',
        }}
      >
        <div
          style={{
            ...previewFrameSize,
            position: 'relative',
            background: 'var(--bg-surface)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <iframe
            ref={iframeRef}
            key={previewDocumentUrl}
            src={previewDocumentUrl}
            onLoad={markPreviewDomReady}
            title="Deck Preview"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: 'var(--bg-surface)' }}
          />
          {exportProgress && <PreviewExportOverlay progress={exportProgress} />}
        </div>
      </div>
      {/* Speaker-notes pane (PP-main parity gap C). Show under the slide
          viewer when not editing, the flag is on, and the active artifact
          is a slide deck-group (HTML or PPTX-SVG — both write .note.md).
          With no signals (legacy decks) the iframe still shows the deck,
          so fall back to showing the pane. */}
      {!editMode && notesPaneEnabled &&
        currentVersion &&
        (!activeArtifactSignal || activeArtifactSignal.kind === 'deck-group') && (
        <SpeakerNotesPane
          deckId={deckId}
          slideNum={currentSlideNum}
          version={currentVersion}
          refreshKey={0}
        />
      )}
      {showShare && <ShareDialog deckId={deckId} onClose={() => setShowShare(false)} />}
      {showMembers && deckId && (() => {
        let currentUserId: string | null = null;
        try {
          currentUserId = JSON.parse(localStorage.getItem('pp_user') || 'null')?.id ?? null;
        } catch {
          currentUserId = null;
        }
        return (
          <MembersDialog
            deckId={deckId}
            workspaceId={workspaceId ?? null}
            currentUserId={currentUserId}
            onClose={() => setShowMembers(false)}
          />
        );
      })()}
      {/* BuildModePicker removed — Stage 4 of #87 alignment. */}
      {/* Anchor-comment popover. Position is fixed in main-window
          coordinates so it stays glued to the clicked iframe element
          even with the chat sidebar; stops propagation so an outside
          click in the iframe doesn't re-trigger the click handler. */}
      {commentPopover && (
        <div
          style={{
            position: 'fixed', left: commentPopover.x, top: commentPopover.y,
            zIndex: 1000, minWidth: 280, maxWidth: 360,
            background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
            borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
            fontSize: 'var(--fs-sm)', color: 'var(--text-primary)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', letterSpacing: '0.02em' }}>
            {commentPopover.anchor}
          </div>
          {commentPopover.elementText && (
            <div style={{
              fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
              fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', maxWidth: '100%',
            }} title={commentPopover.elementText}>
              "{commentPopover.elementText}"
            </div>
          )}
          <textarea
            ref={commentTextRef}
            placeholder="What would you like to change?"
            style={{
              width: '100%', minHeight: 64, resize: 'vertical', boxSizing: 'border-box',
              border: '1px solid var(--border)', borderRadius: 6, padding: 8,
              fontFamily: 'inherit', fontSize: 'var(--fs-sm)', background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendComment(); }
              if (e.key === 'Escape') { e.preventDefault(); closeCommentPopover(); }
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={closeCommentPopover}
              style={{
                padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--fs-sm)',
              }}
            >Cancel</button>
            <button
              onClick={sendComment}
              style={{
                padding: '4px 12px', borderRadius: 6, border: 'none',
                background: 'var(--brand)', color: '#fff', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--fs-sm)', fontWeight: 500,
              }}
            >Send</button>
          </div>
        </div>
      )}
      <style>{`
        .tab-scroll::-webkit-scrollbar { display: none; }
        @container (max-width: 500px) {
          .pp-tab-actions button span { display: none; }
        }
        @media (max-width: 900px) {
          .pp-tab-actions button span { display: none !important; }
          .pp-tab-actions button { padding: 4px 6px !important; }
        }
      `}</style>
    </div>
  );
}

function PreviewExportOverlay({ progress }: { progress: ExportProgressState }) {
  const pct = Math.max(0, Math.min(100, Math.round(progress.progress)));
  const kindLabel = progress.kind.toUpperCase();
  const isError = progress.status === 'error';
  const barColor = isError ? '#D92D20' : 'var(--brand)';
  const statusLabel = progress.status === 'complete'
    ? 'Ready'
    : progress.status === 'error'
      ? 'Failed'
      : `${pct}%`;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${kindLabel} export ${progress.label}`}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(10, 10, 10, 0.10)',
        pointerEvents: 'auto',
      }}
    >
      <div style={{
        width: 'min(360px, 82%)',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '14px 16px 15px',
        boxShadow: '0 10px 32px rgba(0,0,0,0.16)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
          color: 'var(--text-primary)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 500,
          lineHeight: 1.2,
        }}>
          <DownloadSimple size={14} weight="light" style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{kindLabel}</span>
          <span style={{
            marginLeft: 'auto',
            color: isError ? '#D92D20' : 'var(--text-muted)',
            fontSize: 'var(--fs-2xs)',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}>
            {statusLabel}
          </span>
        </div>
        <div style={{
          marginTop: 7,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: isError ? '#D92D20' : 'var(--text-secondary)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 400,
        }}>
          {progress.label}
        </div>
        <div style={{
          height: 4,
          marginTop: 11,
          borderRadius: 999,
          background: 'var(--bg-surface)',
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${pct}%`,
            height: '100%',
            borderRadius: 999,
            background: barColor,
            transition: 'width 180ms ease',
          }} />
        </div>
      </div>
    </div>
  );
}

/**
 * Right-panel overlay for non-deck artifact previews. Sits on top of the
 * PreviewPanel content so the underlying deck iframe stays mounted, then
 * branches by mime type:
 *   - image/* and application/pdf  → inline iframe via artifact proxy
 *   - everything else (incl. pptx) → metadata card with Download button
 */
function ArtifactOverlay({
  deckId,
  artifact,
  onClose,
}: {
  deckId: string;
  artifact: { fileHash?: string; fileUrl?: string; filename: string; fileMime?: string; fileSize?: number };
  onClose: () => void;
}) {
  const mime = artifact.fileMime || '';
  const htmlPreview = mime.split(';', 1)[0]?.trim().toLowerCase() === 'text/html';
  const inlineRenderable = mime.startsWith('image/') || mime === 'application/pdf' || htmlPreview;
  const proxyBase = artifact.fileUrl || `/api/decks/${deckId}/artifacts/${encodeURIComponent(artifact.fileHash || '')}`;
  const inlineUrl = artifact.fileUrl
    ? proxyBase
    : `${proxyBase}${artifact.fileMime ? `?mime=${encodeURIComponent(artifact.fileMime)}` : ''}`;
  const downloadUrl = `${proxyBase}${proxyBase.includes('?') ? '&' : '?'}download=1${!artifact.fileUrl && artifact.fileMime ? `&mime=${encodeURIComponent(artifact.fileMime)}` : ''}`;
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 30,
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--fs-base)', color: 'var(--text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{artifact.filename}</div>
          {htmlPreview && (
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 2 }}>
              HTML artifact preview
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <a
            href={downloadUrl}
            download={artifact.filename}
            style={{
              background: 'var(--brand)', color: '#fff', textDecoration: 'none',
              borderRadius: 4, padding: '4px 10px', fontSize: 'var(--fs-sm)',
            }}
          >{htmlPreview ? 'Download HTML' : 'Download'}</a>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 4, padding: '4px 10px',
              fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >Back to deck</button>
        </div>
      </div>
      {inlineRenderable ? (
        <iframe
          src={inlineUrl}
          title={artifact.filename}
          sandbox={htmlPreview ? 'allow-scripts' : undefined}
          style={{ flex: 1, border: 'none', minHeight: 0, background: htmlPreview ? '#fff' : 'var(--bg-surface)' }}
        />
      ) : (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            padding: 32, minWidth: 280, maxWidth: 480,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)', borderRadius: 8,
          }}>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 500, color: 'var(--text-primary)', textAlign: 'center', wordBreak: 'break-all' }}>
              {artifact.filename}
            </div>
            {artifact.fileSize !== undefined && (
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                {formatFileSize(artifact.fileSize)}
              </div>
            )}
            <a
              href={downloadUrl}
              download={artifact.filename}
              style={{
                background: 'var(--brand)',
                color: '#fff', textDecoration: 'none',
                padding: '8px 24px', borderRadius: 4,
                fontSize: 'var(--fs-base)',
              }}
            >Download</a>
          </div>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
